import { parseGIF, decompressFrames } from 'gifuct-js';
import { IMAGE_GIF_ANIMATED } from 'state/actions';
import { BFAnimatedSprite } from 'utils/BFAnimatedSprite';
import { reportIssue } from 'utils/Tracker';
import { getCloneId, isUrlBlob } from '../utils';
import { FrameData, PictureFrameData } from './FlyerData';
import { Frame } from './Frame';
import { FrameType } from './FrameType';
import Storable from './Storable';

const MAX_CANVAS_WIDTH = 5000;

const validateAndFix = (gif: any) => {
  let currentGce = null;
  for (const frame of gif.frames) {
    currentGce = frame.gce ? frame.gce : currentGce;

    // fix loosing graphic control extension for same frames
    if ('image' in frame && !('gce' in frame)) {
      frame.gce = currentGce;
    }
  }
};

const createImageFromFrames = (
  frames: any[],
  width: number,
  height: number
): string => {
  const columnsNum = Math.floor(MAX_CANVAS_WIDTH / width);
  const numRows = Math.ceil(frames.length / columnsNum);
  const canvas = document.createElement('canvas');
  canvas.width = width * Math.min(columnsNum, frames.length);
  // console.log("Total Width: " + canvas.width);
  canvas.height = height * numRows;
  const ctx = canvas.getContext('2d')!;
  const gifCanvas = document.createElement('canvas');
  gifCanvas.width = width;
  gifCanvas.height = height;
  const gifCtx = gifCanvas.getContext('2d')!;

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;
  const tempCtx = tempCanvas.getContext('2d')!;
  let frameData;
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    if (frame.disposalType === 2) {
      gifCtx.clearRect(0, 0, width, height);
    }
    const col = i % columnsNum;
    const row = Math.floor(i / columnsNum);
    if (
      !frameData ||
      tempCanvas.width !== frame.dims.width ||
      tempCanvas.height !== frame.dims.height
    ) {
      tempCanvas.width = frame.dims.width;
      tempCanvas.height = frame.dims.height;
      frameData = tempCtx!.createImageData(tempCanvas.width, tempCanvas.height);
    }
    frameData.data.set(frame.patch);
    tempCtx.putImageData(frameData, 0, 0);
    gifCtx.drawImage(tempCanvas, frame.dims.left, frame.dims.top);
    ctx.drawImage(gifCanvas, col * width, row * height);
  }
  return canvas.toDataURL('image/png');
};

const parseSpriteSheet = async (sheet: PIXI.Spritesheet): Promise<void> => {
  return new Promise(resolve => {
    sheet.parse(() => resolve());
  });
};

export class PictureFrame extends Frame implements Storable {
  isResizable = true;

  file: File | null = null;
  url: string;
  filename: string;

  get canRotate(): boolean {
    return true;
  }

  async checkSupport(): Promise<boolean> {
    if (!this.isVideo) {
      return true;
    }
    const video = this.texture!.baseTexture.source as HTMLVideoElement;
    return new Promise((resolve: Function, reject: Function) => {
      video.addEventListener('loadedmetadata', event => {
        resolve(video.videoWidth > 0);
      });
    });
  }

  async load() {
    if (this.isLoaded) {
      return;
    }

    // The texture should be created before any wait.
    let source: HTMLImageElement | HTMLVideoElement | null = null;
    if (this.isVideo) {
      const video: HTMLVideoElement = document.createElement('video');
      video.preload = 'auto';
      video.setAttribute('crossorigin', 'anonymous');
      video.loop = true;
      video.muted = true;
      this.texture = PIXI.Texture.fromVideo(video);
      (this.texture!.baseTexture as PIXI.VideoBaseTexture).autoPlay = false;
      source = video;
      // this.texture.baseTexture.autoUpdate = false;
    } else {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      this.texture = PIXI.Texture.from(image);
      source = image;
    }
    if (!this.url) {
      this.url = URL.createObjectURL(this.file!);
    }
    // if (!isUrlBlob(this.url!)) {
    //   const blob = await loadFile(this.url!);
    //   this.url = URL.createObjectURL(blob);
    // }
    // if (this.isClone) {
    //   this.url = await cloneURL(this.url!);
    // }

    source.src = this.url! + (isUrlBlob(this.url) ? '' : `?${Date.now()}`);
    const isSupported = await this.checkSupport();
    if (!isSupported) {
      if (this.isVideo) {
        const video = source as HTMLVideoElement;
        video.pause();
        video.removeAttribute('src'); // empty source
        video.load();
      }
      throw new Error('Picture or video format not supported');
    }
    if (!this.texture!.baseTexture.hasLoaded) {
      await this.waitForBaseTextureLoad();
    }
    this.sprite.texture = this.texture;

    let { width, height } = this.texture;

    source.width = width;
    source.height = height;

    if (!this._boundingBox) {
      if (width > this.stageWidth && height > this.stageHeight) {
        // scale down
        const scaleX = this.stageWidth / width;
        const scaleY = this.stageHeight / height;
        const scale = Math.max(scaleX, scaleY);
        width *= scale;
        height *= scale;
      }

      const x = (this.stageWidth - width) / 2;
      const y = (this.stageHeight - height) / 2;

      this.boundingBox = new PIXI.Rectangle(x, y, width, height);
    } else {
      this.boundingBox = this._boundingBox;
    }

    this.isLoaded = true;
    this.generateThumbnail();
    this.emit('load');

    if (this.type === FrameType.AnimatedGIF) {
      const resp = await fetch(this.url);
      // TODO: use resp to generate thumbnail so that the gif doesn't get loaded twice
      const buff = await resp.arrayBuffer();
      const gif = await parseGIF(buff);
      validateAndFix(gif);
      width = gif.lsd.width;
      height = gif.lsd.height;
      const frames = await decompressFrames(gif, true);
      const textureUrl = createImageFromFrames(frames, width, height);
      const columnsNum = Math.floor(MAX_CANVAS_WIDTH / width);
      const sheetData: any = { frames: {} };
      for (let i = 0; i < frames.length; i++) {
        const col = i % columnsNum;
        const row = Math.floor(i / columnsNum);
        sheetData.frames[`frame ${i}`] = {
          frame: { x: col * width, y: row * height, w: width, h: height },
          rotated: false,
          trimmed: false,
          spriteSourceSize: { x: 0, y: 0, w: width, h: height },
          sourceSize: { w: width, h: height },
          duration: frames[i].delay
        };
      }
      sheetData.meta = {
        app: 'http://www.aseprite.org/',
        version: '1.1.6-dev',
        format: 'RGBA8888',
        size: { w: width, h: height },
        scale: '1',
        frameTags: [],
        layers: [{ name: 'Layer 1', opacity: 255, blendMode: 'normal' }]
      };

      const sheet = new PIXI.Spritesheet(
        (await PIXI.Texture.from(textureUrl)) as any,
        sheetData
      );
      await parseSpriteSheet(sheet);
      const textures: PIXI.FrameObject[] = [];
      const keys = Object.keys(sheet.textures);
      for (const k of keys) {
        const texture = sheet.textures[k];
        const time = sheet.data.frames[k].duration;
        textures.push({ texture, time });
      }

      this.sprite = new BFAnimatedSprite(textures);
      this.sprite.anchor.set(0.5);
      this.sprite.x = this._boundingBox.x + this._boundingBox.width * 0.5;
      this.sprite.y = this._boundingBox.y + this._boundingBox.height * 0.5;
      this.sprite.width = this._boundingBox.width;
      this.sprite.height = this._boundingBox.height;
      this.sprite.angle = this.rotation;

      try {
        this.sprite.gotoAndStop(0, true);
      } catch (e) {
        reportIssue(
          `Could not load GIF ${this.url} because of error ${e.name} - ${e.message}`
        );
        throw new Error(
          'There was a problem loading this sticker. Pleaase try choosing a different one.'
        );
      }
      this.emit('animated');
      document.dispatchEvent(new Event(IMAGE_GIF_ANIMATED));
    }
  }

  setVideoCurrentTime = (time: number): Promise<void> => {
    const video = this.texture!.baseTexture.source as HTMLVideoElement;
    var duration = video.seekable.end(0);
    while (time > duration) {
      time -= duration;
    }
    const MAX_GAP = 1 / 15;
    const MAX_ATTEMPTS = 3;
    let attempt = 0;
    return new Promise(resolve => {
      video.onseeked = () => {
        const gap = Math.abs(video.currentTime - time);
        if (gap > MAX_GAP && attempt < MAX_ATTEMPTS) {
          // try again
          video.currentTime = time;
          attempt++;
          return;
        }
        video.onseeked = null;
        resolve();
      };
      video.currentTime = time;
    });
  };

  generateGIFThumbnail = (): Promise<void> => {
    return new Promise(resolve => {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.onload = () => {
        this.generateThumbnailFromSource(image);
        resolve();
      };
      image.src = this.url;
    });
  };

  populateWithData(data: FrameData) {
    const pictureData = data as PictureFrameData;
    this.url = pictureData.url;
    this.filename = pictureData.name || (this.url!.split('/').pop() as string);
    if (data.type !== undefined) {
      this.type = data.type;
    } else {
      const extension = this.filename.split('.').pop();
      this.type = extension === 'mp4' ? FrameType.Video : FrameType.Image;
    }
    const box = pictureData.box;
    if (box) {
      this._boundingBox = new PIXI.Rectangle(
        box.x,
        box.y,
        box.width,
        box.height
      );
    }
    if (pictureData.rotation) {
      this.rotation = pictureData.rotation;
    }
  }

  asData(forDatabase: boolean): FrameData {
    if (!this.url) {
      throw new Error('Picture frame does not have url');
    }
    if (forDatabase && !this.url.startsWith('http')) {
      throw new Error('Picture frame does not have remote url');
    }
    const { x, y, width, height } = this.boundingBox;
    const result: PictureFrameData = {
      ...super.asData(forDatabase),
      url: this.url,
      box: {
        x,
        y,
        width,
        height
      }
    };
    return result;
  }

  clone(copyIndex: number): Frame {
    const clone = new PictureFrame(this.stageWidth, this.stageHeight);
    clone.id = getCloneId(this.id, copyIndex);
    // const components: string[] = this.filename.split('.');
    // const extension: string = components.pop() as string;
    // clone.filename = `${components.join('.')}-${newUUID()}.${extension}`;
    clone.boundingBox = this.boundingBox;
    clone.filename = this.filename;
    clone.file = this.file;
    clone.url = this.url;
    clone.type = this.type;
    clone.isClone = true;
    clone.backgroundStyle = this.backgroundStyle;
    clone.rotation = this.rotation;
    clone.relativeDuration = this.relativeDuration;
    return clone;
  }

  dispose() {
    // if (this.texture) {
    //   this.texture.destroy(true);
    //   this.texture = null;
    // }
    if (this.sprite) {
      this.sprite.destroy(true);
    }
    // if (this.thumbnailURL) {
    //   URL.revokeObjectURL(this.thumbnailURL);
    // }
    // if (this.url && isUrlBlob(this.url)) {
    //   URL.revokeObjectURL(this.url);
    // }
  }
}



// WEBPACK FOOTER //
// ./src/data/PictureFrame.ts